import { type Flags, PROMOTION_TYPES, VOUCHER_EXTRA_TYPE } from '@/domainLogic/index.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { ConfigValuesApp } from '@/app/common/config/configuration/configurationTypes.ts';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';
import type { Localization } from '../../../common/Localization';
import type { PromotionConfig } from '../types';
import type { Dot } from '../../SearchPageContainer/ListContainer/CardsGrid/types';
import { formatPrice } from '../../../utils/formatting';
import { getVoucherExtraValuesByType, voucherExtraBannerSupported } from '../../../utils/voucherExtra';
import { hasPackageSavingsEnabled } from '../../../utils/productsResponse';

export const isOpxDeal = (dealCard: DealCard) => !!dealCard?.product?.rate?.flags?.opx;
export const hasFlashSales = (dealCard: DealCard) => !!dealCard?.product?.promotions?.flashSale;
export const hasPinkPeople = (dealCard: DealCard) => !!dealCard?.product?.rate?.flags?.pinkPeople;
export const hasTSH = (dealCard: DealCard) => !!dealCard?.product?.rate?.flags?.tsh;
export const hasMyPrice = (dealCard: DealCard) => !!dealCard?.product?.rate?.flags?.myPrice;
export const hasTourDeFrance = (dealCard: DealCard) => !!dealCard?.product?.promotions?.tourFrance;
export const hasBlackFriday = (dealCard: DealCard) => !!dealCard?.product?.promotions?.blackFriday;
export const hasLondonEye = (dealCard: DealCard) => !!dealCard?.product?.promotions?.londonEye;
export const hasTickets = (dealCard: DealCard) => !!dealCard?.product?.rate?.offers?.tickets;
export const hasTransfer = (dealCard: DealCard) => !!dealCard?.product?.rate?.offers?.transfer;
export const hasSkiPass = (dealCard: DealCard) => !!dealCard?.product?.rate?.offers?.skiPass;
export const hasSpa = (dealCard: DealCard) => !!dealCard?.product?.rate?.offers?.spa;
export const hasJanuarySale = (dealCard: DealCard) => !!dealCard?.product?.rate?.offers?.januarySale;
export const hasTEP = (dealCard: DealCard) => !!dealCard?.product?.rate?.tep?.enabled;
export const hasAppOnlyPrice = (dealCard: DealCard, commonData: CommonData, localization: Localization) =>
  commonData.isApp && localization?.configValuesApp?.appOnlyPrice && dealCard?.product?.rate?.flags?.mobile;
export const hasMobileOnlyPrice = (dealCard: DealCard, localization: Localization) =>
  localization?.configValuesApp?.mobileOnlyPrice && dealCard?.product?.rate?.flags?.mobile;
export const hasDepositAmount = (dealCard: DealCard, localization: Localization) =>
  !!dealCard?.product?.rate?.price?.depositAmount && localization?.configValuesApp?.depositAmountEnabled;
export const hasVoucherExtra = (dealCard: DealCard, localization: Localization, flags?: Flags) =>
  voucherExtraBannerSupported(
    VOUCHER_EXTRA_TYPE.THIRTY, // TODO: only 30 is currently supported
    localization?.brand,
    localization?.country,
    localization?.currency
  ) &&
  flags?.voucherExtra &&
  localization?.configValuesApp?.voucherExtraEnabled;
export const hasPackageSavings = (dealCard: DealCard, localization: Localization) =>
  hasPackageSavingsEnabled(dealCard, localization);

export const hasPromotion = (promotionType: PROMOTION_TYPES, promotionTypes: PROMOTION_TYPES[]) =>
  promotionTypes &&
  Array.isArray(promotionTypes) &&
  promotionTypes.length > 0 &&
  promotionTypes.filter((promotion) => promotion === promotionType).length > 0;

const PROMOTION_WITH_PRICE_COLORS = [
  PROMOTION_TYPES.FLASH_SALES,
  PROMOTION_TYPES.JANUARY_SALE,
  PROMOTION_TYPES.MY_PRICE,
  PROMOTION_TYPES.TOP_SECRET_HOTELS,
];

const getFirstPromotionWithColors = (promotions: PROMOTION_TYPES[]) =>
  promotions.find((promotion) => PROMOTION_WITH_PRICE_COLORS.includes(promotion)) || null;

export const getPromotionTheme = (promotions: PROMOTION_TYPES[]): PROMOTION_TYPES | null =>
  promotions && promotions.length > 0 ? getFirstPromotionWithColors(promotions) : null;

export const getDotPromotionType = ({ dot }: { dot: Dot }): PROMOTION_TYPES | undefined => {
  const { myPrice, flashSale } = dot;
  if (flashSale) return PROMOTION_TYPES.FLASH_SALES;
  if (myPrice) return PROMOTION_TYPES.MY_PRICE;
  return undefined;
};

export const getVoucherExtraPromotionValues = (localization: Localization) => {
  const { brand, country, currency } = localization;
  const type = VOUCHER_EXTRA_TYPE.THIRTY; // TODO: only 30 is currently supported
  const voucherExtraValues = getVoucherExtraValuesByType(type, brand, country, currency);
  const voucherCode = voucherExtraValues?.voucherCode;
  const discountAmount = voucherExtraValues?.discountAmount ?? 0;

  return {
    voucherCode,
    discountAmount: formatPrice({
      price: discountAmount,
      localization,
    }),
  };
};

export const swapVoucherExtraAndFlashSalesInMobile = (promotions: PromotionConfig[]) => {
  const flashSalesItem = promotions.find((promotion) => promotion.promotionType === PROMOTION_TYPES.FLASH_SALES);
  const voucherExtraItem = promotions.find((promotion) => promotion.promotionType === PROMOTION_TYPES.VOUCHER_EXTRA);

  if (!flashSalesItem || !voucherExtraItem) {
    return promotions;
  }

  return promotions.map((promotion) => {
    if (promotion.promotionType === PROMOTION_TYPES.FLASH_SALES) {
      return { ...voucherExtraItem };
    }

    if (promotion.promotionType === PROMOTION_TYPES.VOUCHER_EXTRA) {
      return { ...flashSalesItem };
    }

    return promotion;
  });
};

type IsMobileOnlyPriceEnabledProps = {
  configValuesApp?: ConfigValuesApp;
  isMobileDevice: boolean;
  isMeta: boolean;
  isApp: boolean;
};

export const isMobileOnlyPriceEnabled = ({
  configValuesApp,
  isMobileDevice,
  isMeta,
  isApp,
}: IsMobileOnlyPriceEnabledProps) => configValuesApp?.mobileOnlyPrice && isMobileDevice && !isMeta && !isApp;

export const getMobileRates = ({
  configValuesApp,
  isMobileDevice,
  isMeta,
  isApp,
  isHotelOnly,
}: IsMobileOnlyPriceEnabledProps & { isHotelOnly: boolean }) => {
  const isAppAndOnlyPrice = configValuesApp?.appOnlyPrice && isApp;
  const isMobileOnlyPrice = isMobileOnlyPriceEnabled({ configValuesApp, isMobileDevice, isMeta, isApp });
  return (isAppAndOnlyPrice || isMobileOnlyPrice) && isHotelOnly;
};
